export interface FetchResponse<T> {
  data: T | null;
  dataText: string;
  status: number;
  statusText: string;
  ok: boolean;
  error?: unknown;
}

// The app may be served under a path prefix (/account on a Chorus cell); its api lives beside it
const API_BASE = import.meta.env.BASE_URL.replace(/\/$/, "");

export function apiUrl(inUrl: string): string {
  return inUrl === "/api" || inUrl.startsWith("/api/") ? `${API_BASE}${inUrl}` : inUrl;
}
/**
 * Helper function to handle fetch operations with null response handling
 * @param url - The URL to fetch from
 * @param options - Fetch options (optional)
 * @returns Promise that resolves to the parsed JSON or null if the response is not ok or null
 */
export async function fetchWithNullHandling<T>(url: string, options?: RequestInit): Promise<FetchResponse<T>> {
  const result: FetchResponse<T> = {
    data: null,
    dataText: "",
    status: 0,
    statusText: "",
    ok: false
  };
  try {
    const response = await fetch(apiUrl(url), options);

    result.status = response.status;
    result.statusText = response.statusText;
    result.ok = response.ok;

    // Check if response has content before trying to parse JSON
    const text = await response.text();
    result.dataText = text;
    if(!text || text.trim() === "") {
      console.warn(`Empty response from ${url}`);
      return result;
    }

    // Try to parse the JSON
    try {
      const data = JSON.parse(text);
      result.data = data;
      if(data === null) {
        console.warn(`Received null response from ${url}`);
      }
    }
    catch(parseError) {
      console.warn(`Invalid JSON response from ${url}:`, parseError);
    }

    if(!result.ok) {
      console.warn(`Non-ok response from ${url}: `, result.status, result.data || result.dataText);
    }

    return result;
  }
  catch(error) {
    console.error(`Error fetching from ${url}:`, error);
    result.statusText = "Error fetching from server";
    result.error = error;
    return result;
  }
};

/**
 * * Helper function to construct URL params
 * * @param params - parameters object
 * * @returns string representation of url object
 * */
export function buildQueryString(params: Record<string, string | number | boolean | null | undefined>): string {
  const searchParams = new URLSearchParams();

  for(const [key, value] of Object.entries(params)) {
    if(value !== undefined && value !== null) {
      searchParams.append(key, String(value));
    }
  }

  return searchParams.toString();
};
